import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientMaster, ReplacementConflict, ReplacementPreview } from '@Models';
import { bopsApi } from '@Network';

/**
 * A replace the server refused because the file was edited since.
 *
 * Carries who edited it and when, so the view can ask the person whether
 * to overwrite rather than show a flat message. Distinct from an ordinary
 * `Error` precisely so the view can tell "ask the person" from "tell the
 * person it failed".
 */
export class ReplacementConflictError extends Error {
  readonly conflict: ReplacementConflict;

  constructor(conflict: ReplacementConflict) {
    super('The file was edited since this one was prepared.');
    this.name = 'ReplacementConflictError';
    this.conflict = conflict;
    // Restored because the build targets a level where `extends Error`
    // leaves the prototype as Error's, which makes `instanceof` on this
    // subclass answer false -- the whole point of the class is that the
    // view can tell it apart.
    Object.setPrototypeOf(this, ReplacementConflictError.prototype);
  }
}

/**
 * The conflict body a 409 carries, or null if this is some other error.
 *
 * Read off the shape rather than through the transport library, same as
 * `messageFor`. A conflict detail is an object with a `modified_at`; a
 * plain refusal carries a string, which is not one of these.
 */
function conflictFrom(error: unknown): ReplacementConflict | null {
  const refused = error as { response?: { status?: number; data?: { detail?: unknown } } } | null;

  if (refused?.response?.status !== 409) return null;

  const detail = refused.response.data?.detail;

  if (detail !== null && typeof detail === 'object' && 'modified_at' in detail) {
    return detail as ReplacementConflict;
  }

  return null;
}

/**
 * What the server said went wrong, or a sentence of ours.
 *
 * Every refusal these endpoints make is something the person can act on
 * — the name is taken, the file is open, it is not the file they
 * approved — so the server's own wording is better than anything
 * generic. The fallback is for the failures that reach no handler.
 *
 * Read off the shape rather than through the transport library: which
 * one carries the request is `bopsApi`'s business, and a hook that knows
 * is a hook that has to change when that does. A `detail` that is not a
 * sentence is ignored, because a validation failure answers with a list
 * of objects and showing it reads as "[object Object]".
 */
function messageFor(error: unknown, fallback: string): string {
  const refused = error as { response?: { data?: { detail?: unknown } } } | null;
  const detail = refused?.response?.data?.detail;

  if (typeof detail === 'string' && detail.length > 0) return detail;

  return fallback;
}

/**
 * The master operations a client can perform on their own files.
 *
 * Here rather than in the components so that what reaches a view is a
 * promise and a sentence: the view decides what to show, and nothing
 * about how the request is shaped or how a refusal is read is written
 * twice.
 *
 * Each one keeps its identity between renders, so a caller can put it in
 * a dependency list -- the table's columns are memoised against the
 * handlers that close over these.
 */
export default function useMasters() {
  const { t } = useTranslation();

  const createMaster = useCallback(
    async (file: File, masterType: string, sourceId: string | null): Promise<ClientMaster> => {
      try {
        return await bopsApi.createMaster(file, masterType, sourceId);
      } catch (error) {
        throw new Error(messageFor(error, t('Configuration.NewMasterError')));
      }
    },
    [t],
  );

  /** What replacing this master's file would change. Writes nothing. */
  const previewReplacement = useCallback(
    async (masterId: string, file: File): Promise<ReplacementPreview> => {
      try {
        return await bopsApi.previewMasterReplacement(masterId, file);
      } catch (error) {
        throw new Error(messageFor(error, t('Configuration.Error')));
      }
    },
    [t],
  );

  /**
   * Write the file whose changes were approved.
   *
   * `contentHash` is what the preview returned for those bytes; the
   * server refuses any others, so what lands is what was read.
   *
   * A conflict -- the file was edited since -- is thrown as a
   * `ReplacementConflictError` so the view can offer the person the
   * choice to overwrite. `force` is that choice, sent back on the retry.
   */
  const replaceContent = useCallback(
    async (masterId: string, file: File, contentHash: string, force = false): Promise<void> => {
      try {
        await bopsApi.replaceMasterContent(masterId, file, contentHash, force);
      } catch (error) {
        const conflict = conflictFrom(error);

        if (conflict) throw new ReplacementConflictError(conflict);

        throw new Error(messageFor(error, t('Configuration.Error')));
      }
    },
    [t],
  );

  return { createMaster, previewReplacement, replaceContent };
}
